import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { parseTime } from "@shared/nz-time";
import { cx } from "@shared/cx";
import { useDismissable } from "../../lib/use-dismissable";
import { InlineBanner } from "./InlineBanner";
import styles from "./TimeInput.module.css";

const timeSuggestions: string[] = [];
for (let hour = 6; hour <= 26; hour++) {
  const displayHour = hour >= 24 ? hour - 24 : hour;
  const period = hour >= 12 && hour < 24 ? "PM" : "AM";
  const hour12 =
    displayHour === 0 ? 12 : displayHour > 12 ? displayHour - 12 : displayHour;
  timeSuggestions.push(`${hour12}:00 ${period}`, `${hour12}:30 ${period}`);
}

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  /**
   * The field's own shape, from the call site: this control is a full-width
   * form field in one place and a table cell's inline-edit input in another,
   * so the box is the caller's and only the states are this component's.
   */
  inputStyles: string;
  disabled?: boolean;
}

function displayValue(value: string): string {
  return parseTime(value) ?? value;
}

export function TimeInput({
  value,
  onChange,
  ariaLabel,
  inputStyles,
  disabled,
}: TimeInputProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => displayValue(value));
  const [parseError, setParseError] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useEffect(() => setInputValue(displayValue(value)), [value]);

  const filteredSuggestions = inputValue
    ? timeSuggestions.filter((time) =>
        time
          .toLowerCase()
          .replace(/\s/g, "")
          .includes(inputValue.toLowerCase().replace(/\s/g, "")),
      )
    : timeSuggestions;

  // The suggestion list is portaled to <body>, so "inside" is the input AND
  // the dropdown, not one wrapper.
  useDismissable({
    open: isOpen,
    onDismiss: () => setIsOpen(false),
    pointer: [inputRef, dropdownRef],
  });

  useEffect(() => {
    if (!isOpen) return;
    const positionDropdown = (): void => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openAbove = spaceBelow < 120 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(
        80,
        Math.min(192, openAbove ? spaceAbove : spaceBelow),
      );
      setDropdownStyle({
        position: "fixed",
        left: rect.left,
        top: openAbove
          ? Math.max(gap, rect.top - maxHeight - gap)
          : rect.bottom + gap,
        width: rect.width,
        maxHeight,
        zIndex: 1000,
      });
    };
    positionDropdown();
    window.addEventListener("resize", positionDropdown);
    window.addEventListener("scroll", positionDropdown, true);
    return () => {
      window.removeEventListener("resize", positionDropdown);
      window.removeEventListener("scroll", positionDropdown, true);
    };
  }, [isOpen, filteredSuggestions.length]);

  const selectTime = (time: string): void => {
    setInputValue(time);
    setParseError(false);
    onChange(time);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleBlur = (): void => {
    const parsed = parseTime(inputValue);
    if (parsed === null) {
      setParseError(true);
      return;
    }
    setParseError(false);
    if (parsed !== inputValue) {
      setInputValue(parsed);
      onChange(parsed);
    }
  };

  // Escape is handled by useDismissable.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter" && filteredSuggestions.length > 0) {
      event.preventDefault();
      selectTime(filteredSuggestions[0]);
    }
  };

  return (
    <div className={styles.wrapper}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={isOpen && filteredSuggestions.length > 0}
        aria-controls={
          isOpen && filteredSuggestions.length > 0 ? listboxId : undefined
        }
        aria-invalid={parseError || undefined}
        value={inputValue}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setInputValue(event.target.value);
          setParseError(false);
          setIsOpen(true);
          onChange(event.target.value);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cx(inputStyles, styles.input, parseError && styles.invalid)}
      />
      {parseError && (
        <InlineBanner variant="error">
          Enter a valid time (e.g. 8:00 PM) or leave blank
        </InlineBanner>
      )}
      {isOpen &&
        filteredSuggestions.length > 0 &&
        createPortal(
          <div
            ref={dropdownRef}
            id={listboxId}
            role="listbox"
            aria-label={`${ariaLabel} suggestions`}
            style={dropdownStyle}
            className={styles.listbox}
          >
            {filteredSuggestions.map((time) => (
              <div
                key={time}
                role="option"
                aria-selected={time === inputValue}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectTime(time)}
                className={styles.option}
              >
                {time}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
